import React from 'react';
import { View } from 'react-native';
import { Button, Card, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import type { StandardTermsData } from './StandardTerms';

interface Props {
  code: string | null; loading: boolean; error: boolean; active: boolean; suspended: boolean;
  onCopy: () => void; onRetry: () => void;
  /** Validated server-composed share link for `code`, or null when unavailable. */
  shareUrl?: string | null; onCopyLink?: () => void;
  terms?: StandardTermsData | null; regime?: string | null;
  /** No shareable code yet: the partner still makes the single initial choice (F1). */
  pendingChoice?: boolean;
}

const num = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/**
 * The partner's ONE stable, server-owned code (D-057 single programme). The code
 * and its canonical share link are shown and copied verbatim from the API; they
 * are never derived or invented here.
 */
export function ReferralCodeCard({ code, loading, error, active, suspended, onCopy, onRetry, shareUrl, onCopyLink, terms, regime, pendingChoice }: Props) {
  const { t, i18n } = useTranslation('partner');
  const theme = useTheme();
  const days = num(terms?.monthly_trial_days, 14);
  const total = num(terms?.intended_total_percent, 20);
  // Display only: in the normal VAT regime the 20% total includes partner VAT,
  // so the pre-VAT rate is shown beside it (D-057). Exempt or unknown regimes
  // keep the plain total; the rate is never inferred.
  const rate = regime === 'normal'
    ? new Intl.NumberFormat(i18n.language || 'pt-PT', { maximumFractionDigits: 2 }).format(num(terms?.rate_percent, 16.26))
    : null;
  const touch = { minHeight: 44 };
  const ready = !loading && !error && !!code;
  return <Card testID="referral-code-card" style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12, paddingVertical: 12 }}>
    <Text variant="titleLarge">{t('codeTitle')}</Text>
    <Text testID="code-brief" variant="bodyMedium">{rate ? t('codeBriefNormal', { days, total, rate }) : t('codeBrief', { days, total })}</Text>
    {loading && <Text>{t('codeLoading')}</Text>}
    {!loading && !error && !code && pendingChoice && <Text testID="code-pending-choice">{t('codePendingChoice')}</Text>}
    {!loading && (error || (!code && !pendingChoice)) && <View style={{ gap: 8 }}>
      <Text testID="code-unavailable" accessibilityRole="alert" style={{ color: theme.colors.error }}>{t('codeUnavailable')}</Text>
      <Button testID="code-retry" mode="outlined" icon="refresh" contentStyle={touch} onPress={onRetry}>{t('codeRetry')}</Button>
    </View>}
    {ready && <Text testID="referral-code-value" selectable variant="headlineMedium">{code}</Text>}
    <Button testID="copy-code" mode="contained" icon="content-copy" contentStyle={touch}
      disabled={!ready || !active} onPress={onCopy}>{t('copyCodeAction')}</Button>
    {ready && !!shareUrl && <View testID="share-link" style={{ gap: 8 }}>
      <Text variant="labelLarge">{t('linkLabel')}</Text>
      <Text testID="share-link-value" selectable variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>{shareUrl}</Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('linkHelp')}</Text>
      <Button testID="copy-link" mode="outlined" icon="link-variant" contentStyle={touch}
        disabled={!active || !onCopyLink} onPress={onCopyLink}>{t('copyLinkAction')}</Button>
    </View>}
    {suspended && !loading && <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{t('codeSuspended')}</Text>}
  </Card.Content></Card>;
}
